import React from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';
import mongoDbLogo from '../assets/images/partners/mongo_db.png';
import awsLogo from '../assets/images/partners/aws-partner.png';
import salesforceLogo from '../assets/images/partners/salesforce.png';
import gitlabLogo from '../assets/images/partners/gitlab.png';
import googleLogo from '../assets/images/partners/google-cloud-partner.png';
import microsoftLogo from '../assets/images/partners/microsoft-partner.png';
import arizonaTechLogo from '../assets/images/partners/arizona-technology-council-partner.png';
import cuembyLogo from '../assets/images/partners/cuemby.svg';

const PricingSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  return (
    <section>
      <Box
        sx={{
          width: '100vw',
          minHeight: { xs: 'auto', md: '1180px' },
          position: 'relative',
          background: 'black',
          marginLeft: 'calc(-50vw + 50%)',
          marginRight: 'calc(-50vw + 50%)',
          display: 'flex',
          justifyContent: 'center',
          py: { xs: 6, md: 8, lg: 0 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at top right, rgba(191, 109, 255, 0.15), transparent 60%), radial-gradient(circle at bottom left, rgba(109, 252, 255, 0.15), transparent 60%)',
            pointerEvents: 'none',
            zIndex: 2,
          },
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          outline: '1px solid black',
          outlineOffset: '-1px',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '1140px',
            position: 'relative',
            zIndex: 3,
            paddingTop: { xs: '50px', sm: '100px', md: '150px' },
            paddingX: { xs: 2, sm: 3, md: 4 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: { xs: '40px', sm: '50px', md: '75px' },
          }}
        >
          {/* Title Section */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: '10px', md: '5px' },
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontSize: { xs: '28px', sm: '34px', md: '42px' },
                fontFamily: 'Raleway',
                fontWeight: 600,
                lineHeight: { xs: '36px', sm: '46px', md: '58px' },
              }}
            >
              Meet Our Solution Partners
            </Typography>
            <Typography
              sx={{
                color: 'white',
                fontSize: { xs: '14px', md: '16px' },
                fontFamily: 'Raleway',
                fontWeight: 400,
                maxWidth: '600px',
                px: { xs: 2, sm: 0 },
              }}
            >
              We are partners to many of the major service providers for Cloud, CRM, DevOps and Database development companies and have built our own solutions as well.
            </Typography>
          </Box>

          {/* Cards Grid */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: { xs: '30px', sm: '24px' },
              justifyContent: 'center',
            }}
          >
            {/* MongoDB COE */}
            <PartnerCard 
              logo={mongoDbLogo} 
              title="We are MongoDB COE" 
              description="We can help your organization adopting/adapting and enhancing your MongoDB code to better suit your business needs."
              bgColor="#F1F1F1"
              logoWidth="200px"
              logoHeight="73px"
              isMobile={isMobile}
            />

            {/* Amazon Partners */}
            <PartnerCard 
              logo={awsLogo} 
              title="Amazon Partners" 
              description="Contact us for help with your AWS stack if you need help setting up, configuring and maintaining AWS resources ( EC2, S3, VPC, CloudFront, RDS ,etc)"
              bgColor="#F1F1F1"
              logoWidth="180px"
              logoHeight="auto"
              hasUnderline={true}
              underlineText="AWS"
              isMobile={isMobile}
            />

            {/* Salesforce Partners */}
            <PartnerCard 
              logo={salesforceLogo} 
              title="Salesforce Partners" 
              description="We are part of the Salesforce partner community! Contact us for your consulting needs with Salesforce related tasks (Administration, Consulting, Development & Training)."
              bgColor="#303030"
              logoWidth="205px"
              logoHeight="82px"
              hasUnderline={true}
              underlineText="Salesforce"
              isMobile={isMobile}
            />

            {/* GitLab Partners */}
            <PartnerCard 
              logo={gitlabLogo} 
              title="GitLab Partners" 
              description="GitLab Tech Partner with our premier product management app linked to GitLab (Gitdone.io), offering a more holistic product management and road-mapping tool."
              bgColor="#303030"
              logoWidth="110px"
              logoHeight="101px"
              hasUnderline={true}
              underlineText="GitLab"
              secondUnderlineText="(Gitdone.io)"
              isMobile={isMobile}
            />

            {/* Google Cloud */}
            <PartnerCard 
              logo={googleLogo} 
              title="Google Cloud" 
              description="Contact us if you need help setting up, configuring and maintaining GCP resources (Compute Engine, Cloud Storage, CloudSQL, etc)"
              bgColor="white"
              logoWidth="208px"
              logoHeight="101px"
              hasUnderline={true}
              underlineText="Google"
              isMobile={isMobile}
            />

            {/* Microsoft Partner */}
            <PartnerCard 
              logo={microsoftLogo} 
              title="Microsoft Partner" 
              description="Contact us for help with your Dynamics 365 and any custom .NET development needs your company has!"
              bgColor="white"
              logoWidth="211px"
              logoHeight="85px"
              hasUnderline={true}
              underlineText="Dynamics"
              isMobile={isMobile}
            />

            {/* Arizona Tech Council */}
            <PartnerCard 
              logo={arizonaTechLogo} 
              title="Arizona Tech Council" 
              description="We are proud members of the broader Arizona blossoming technological community bringing highly innovative solutions to businesses in the US and the state of Arizona."
              bgColor="#303030"
              logoWidth="192px"
              logoHeight="48px"
              hasUnderline={true}
              underlineText="community"
              isMobile={isMobile}
            />

            {/* Cuemby */}
            <PartnerCard 
              logo={cuembyLogo} 
              title="Cuemby" 
              description="We partnered up with Cuemby to help sell and implement their private cloud solutions and comprehensive hands-on training on Cuemby's cloud-native solutions"
              bgColor="#303030"
              logoWidth="193px"
              logoHeight="44px"
              isMobile={isMobile}
            />
          </Box>
        </Box>
      </Box>
    </section>
  );
};

// Partner Card Component for DRY code
const PartnerCard = ({ 
  logo, 
  title, 
  description, 
  bgColor, 
  logoWidth, 
  logoHeight, 
  hasUnderline = false, 
  underlineText = '', 
  secondUnderlineText = '',
  isMobile
}) => {
  // Generate URL based on title
  const generateUrl = () => {
    const urls = {
      "We are MongoDB COE": "https://www.mongodb.com/partners/find-a-partner",
      "Amazon Partners": "https://aws.amazon.com/partners/",
      "Salesforce Partners": "https://www.salesforce.com/partners/",
      "GitLab Partners": "https://about.gitlab.com/partners/",
      "Google Cloud": "https://cloud.google.com/find-a-partner",
      "Microsoft Partner": "https://www.microsoft.com/en-us/solution-providers/home",
      "Arizona Tech Council": "https://www.aztechcouncil.org/",
      "Cuemby": "https://cuemby.com/",
    };

    return urls[title] || "#";
  };

  // Handle description text with optional underlined text
  const renderDescription = () => {
    if (!hasUnderline) return description;
    
    const parts = description.split(underlineText);
    let result = [];
    
    parts.forEach((part, index) => {
      if (index > 0) {
        result.push(
          <Box component="span" key={`underline-${index}`} sx={{ textDecoration: 'underline' }}>
            {underlineText}
          </Box>
        );
      }
      result.push(part);
    });
    
    if (secondUnderlineText) {
      const newResult = [];
      result.forEach(item => {
        if (typeof item === 'string') {
          const subParts = item.split(secondUnderlineText);
          subParts.forEach((subPart, idx) => {
            if (idx > 0) {
              newResult.push(
                <Box component="span" key={`second-underline-${idx}`} sx={{ textDecoration: 'underline' }}>
                  {secondUnderlineText}
                </Box>
              );
            }
            newResult.push(subPart);
          });
        } else {
          newResult.push(item);
        }
      });
      result = newResult;
    }
    
    return result;
  };

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 'calc(50% - 12px)', md: '358px' },
        height: '288px',
        padding: '30px',
        background: bgColor || 'white',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        maxWidth: { xs: '358px', sm: '358px' },
      }}
    >
      <Box
        component="a"
        href={generateUrl()}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'opacity 0.3s ease',
          '&:hover': {
            opacity: 0.85,
          },
          textDecoration: 'none',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt={`${title} logo`}
          sx={{
            width: logoWidth || 'auto',
            height: logoHeight || 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>
      
      <Box>
        <Typography
          sx={{
            fontSize: { xs: '20px', md: '20px' },
            fontWeight: 700,
            fontFamily: 'Raleway',
            color: bgColor === '#303030' ? 'white' : 'black',
            marginBottom: '10px',
          }}
        >
          {title}
        </Typography>
        
        <Typography
          sx={{
            fontSize: { xs: '14px', md: '16px' },
            fontWeight: 400,
            fontFamily: 'Raleway',
            color: bgColor === '#303030' ? 'white' : 'black',
            lineHeight: '25px',
          }}
        >
          {renderDescription()}
        </Typography>
      </Box>
    </Box>
  );
};

export default PricingSection; 